import React, { useState, useEffect, useCallback } from 'react'
import { useToast, Stack, Textarea, Heading, Text, Badge, Button, Box, Progress, CircularProgress, CircularProgressLabel, Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverHeader, PopoverFooter, Alert, AlertTitle, AlertDescription, Modal, ModalBody, ModalContent, ModalOverlay, ModalHeader, ModalCloseButton, FormControl, Select, Divider, Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Icon, UnorderedList, ListItem } from '@chakra-ui/react'
import { CheckIcon, CheckCircleIcon, WarningIcon } from '@chakra-ui/icons'
import { FaFilePdf, FaFileWord, FaCaretDown } from 'react-icons/fa'
import 'react-datepicker/dist/react-datepicker.css'
import api from '../../../api'
import utils from '../../../utils/utils'

import ConfirmButton from '../../includes/ConfirmButton'
import RichTextViewer from '../../includes/RichTextViewer'
import SimpleMenu from '../../includes/SimpleMenu'

import ShortText from '../../fields/ShortText'
import LongText from '../../fields/LongText'
import Integer from '../../fields/Integer'
import Float from '../../fields/Float'
import SliderField from '../../fields/Slider'
import Choice from '../../fields/Choice'
import DateInput from '../../fields/Date'
import DatetimeInput from '../../fields/Datetime'
import Temperature from '../../fields/Temperature'
import Height from '../../fields/Height'
import Weight from '../../fields/Weight'
import BMI from '../../fields/BMI'
import RAVLT from '../../fields/RAVLT'
import File from '../../fields/File'
import Image from '../../fields/Image'
import Video from '../../fields/Video'
import Food from '../../fields/Food'

const FIELDS = {
  text: ShortText,
  paragraph: LongText,
  integer: Integer,
  float: Float,
  slider: SliderField,
  choice: Choice,
  date: DateInput,
  datetime: DatetimeInput,
  temperature: Temperature,
  height: Height,
  weight: Weight,
  bmi: BMI,
  file: File,
  image: Image,
  video: Video,
  food: Food,
  ravlt: RAVLT
}

export default function ResponseEditor ({ trialId, stageId, participantId, isOpen, onClose, onSubmit, previewComponents }) {
  const [stage, setStage] = useState()
  const [components, setComponents] = useState([])
  const [participant, setParticipant] = useState()
  const [responses, setResponses] = useState([])
  const [drafts, setDrafts] = useState([])
  const [dataTypes, setDataTypes] = useState([])
  const [response, setResponse] = useState({})
  const [updateNote, setUpdateNote] = useState('')
  const [existingResponse, setExistingResponse] = useState({})
  const [recurrence, setRecurrence] = useState(0)
  const [changesMade, setChangesMade] = useState(false)
  const [error, setError] = useState()
  const [errorField, setErrorField] = useState()
  const [exporting, setExporting] = useState(false)
  const toast = useToast()
  const isPreview = previewComponents?.length > 0

  // If this is a preview, then load the preview components
  useEffect(() => {
    if (previewComponents?.length) {
      setComponents(previewComponents)
    }
  }, [previewComponents])

  // Fetch the data types when the trialId changes
  useEffect(() => {
    if (trialId) {
      api.trials.getDataTypes(trialId, d => setDataTypes(d.dataTypes))
    }
  }, [trialId])

  // Fetch the responses when the stageId or participantId changes
  useEffect(() => {
    if (stageId && participantId) {
      api.trials.getParticipantResponses(trialId, participantId, ({ drafts, responses, participant }) => {
        setDrafts(drafts)
        setResponses(responses)
        setParticipant(participant)
      })
    }
  }, [stageId, participantId])

  // Fetch the stage when the stageId changes
  useEffect(() => {
    if (stageId) {
      api.stages.get(stageId, setStage)
    }
  }, [stageId])

  // Build a component list based on the data type objects
  useEffect(() => {
    if (stage?.components?.length && dataTypes?.length) {
      setComponents(stage.components.map(c => {
        const dt = dataTypes.find(d => d._id === c.dataType)
        if (dt) return { ...c, type: dt }
        return c
      }))
    }
  }, [stage, dataTypes])

  // If stage/recurrence change, reload the existing/draft response for this stage/recurrence
  useEffect(() => {
    if (stage?.isRecurring && !stage?.adHoc) { // Recurring stage. Grab the response with stage and recurrence
      const stageResponsesForRecurrence = responses?.filter(r => r.stage === stage?._id && ((r.stageRecurrence || 0) === (recurrence || 0)))
      if (stageResponsesForRecurrence?.length && stageResponsesForRecurrence[0]?.response) setExistingResponse(stageResponsesForRecurrence[0]?.response)
      else setExistingResponse({})
    }
    if (!stage?.isRecurring && !stage?.adHoc) { // One-off stage. Grab the response matching the stage
      const stageResponses = responses?.filter(r => r.stage === stage?._id && !r.stageRecurrence)
      if (stageResponses?.length && stageResponses[0]?.response) setExistingResponse(stageResponses[0]?.response)
      else setExistingResponse({})
    }
    if (stage?.adHoc) setExistingResponse({}) // Ad-hoc stage. Create a new response.

    const stageDraft = drafts?.find(r => r.stage === stage?._id && (r.stageRecurrence || 0) === (recurrence || 0))?.response
    setResponse(stageDraft ?? {})
  }, [recurrence, stage, responses])

  // Create a new draft with the response argument
  const saveDraft = useCallback(utils.debounce((resp) => {
    setError()
    const stageIdWithRecurrence = `${stage._id}${recurrence > 0 ? `::${recurrence}` : ''}`
    api.trials.updateParticipantResponseDraft(trialId, participant._id, stageIdWithRecurrence, { response: resp }, newDraft => {
      setChangesMade(false)

      // Save draft locally to prevent re-fetching
      let found = false
      const newDrafts = drafts?.map(d => {
        if (d.stage === newDraft.stage && (d.stageRecurrence || 0) === (newDraft.stageRecurrence || 0)) {
          found = true
          return newDraft
        }
        return d
      })
      if (!found) {
        newDrafts.push(newDraft)
      }
      setDrafts(newDrafts)
    }, err => {
      setError(err.message)
    })
  }), [stage, recurrence, participant, drafts]) // Refresh function if drafts change (important)

  // Delete the draft for this stage and reset responses
  const revertDraft = () => {
    if (isPreview) return setResponse({})
    const stageIdWithRecurrence = `${stage._id}${recurrence > 0 ? `::${recurrence}` : ''}`
    api.trials.deleteParticipantResponseDraft(trialId, participant._id, stageIdWithRecurrence, () => {
      setDrafts(drafts?.filter(d => !(d.stage === stageId && (d.stageRecurrence || 0) === (recurrence || 0))))
      setResponse({})
      setError()
    }, err => {
      toast({ title: 'Unable to delete the draft', description: err.message, status: 'error' })
      setError(err.message)
    })
  }

  // Send data off to submit for real
  const submit = () => {
    setError()
    setErrorField()
    const stageId = `${stage._id}${recurrence > 0 ? `::${recurrence}` : ''}`
    api.trials.updateParticipantResponse(trialId, participant._id, stageId, { response, updateNote }, () => {
      setUpdateNote('')
      onSubmit()
      onClose()
      toast({ title: 'Done', description: 'The participant\'s data has been updated', status: 'success' })
    }, err => {
      if (err.message.indexOf('[') > -1) {
        const match = err.message.match(/\[([a-z0-9-]+)\]/i)
        if (match?.length > 1) {
          setErrorField(match[1])
          err.message = err.message.replace('[' + match[1] + ']', '')
          document.getElementById(`field-entry-${match[1]}`).scrollIntoView({ behavior: 'smooth' })
        }
      }
      setError(err.message)
    })
  }

  function fieldHasValue (dataTypeId) {
    const value = response[dataTypeId]?.value || existingResponse[dataTypeId]?.value
    if (!value) return false
    if (Array.isArray(value)) return value.length > 0
    return true
  }

  function conditionIsMet (c) {
    if (!c.condition) return true
    const conditionResp = response[c.condition.dataType]?.value ?? existingResponse[c.condition.dataType]?.value

    const exists =
      conditionResp !== null && conditionResp !== undefined && conditionResp !== '' &&
      (Array.isArray(conditionResp) ? (conditionResp?.length > 0) : true)

    const isEqual =
      conditionResp?.toString()?.toLowerCase()?.trim() === c.condition.value?.toString()?.toLowerCase()?.trim() ||
      (Array.isArray(c.condition?.value) && c.condition?.value.indexOf(conditionResp) > -1) ||
      (Array.isArray(conditionResp) && conditionResp.indexOf(c.condition?.value) > -1) ||
      (Array.isArray(c.condition?.value) && Array.isArray(conditionResp) && c.condition.value.filter(x => conditionResp?.indexOf(x) !== -1)?.length > 0)

    return (
      (c.condition.operator === 'exists' && exists) ||
      (c.condition.operator === 'equal' && isEqual) ||
      (c.condition.operator === 'notequal' && !isEqual) ||
      (c.condition.operator === 'more' && conditionResp > c.condition.value) ||
      (c.condition.operator === 'less' && conditionResp < c.condition.value)
    )
  }

  function exportStage (format) {
    setExporting(true)
    api.stages.getWithFormat(stageId, format, pdf => {
      const link = document.createElement('a')
      link.href = pdf?.url
      link.target = '_blank'
      link.setAttribute('download', `${stage.name}.pdf`)
      document.body.appendChild(link)
      link.click()
      setExporting(false)
    }, err => {
      toast({ title: 'Unable to export stage', description: err.message, status: 'error' })
      setExporting(false)
    })
  }

  function closeModal () {
    if (changesMade) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close this window?')) onClose()
    } else {
      onClose()
    }
  }

  // Extract some useful objects for this stage
  const stageResponses = responses?.filter(r => r?.stage === stage?._id)
  const stageResponsesForRecurrence = stageResponses?.filter(r => (r.stageRecurrence || 0) === (recurrence || 0))

  // Process sections separated by sectionDivider components
  const sections = []
  components?.forEach((c, i) => {
    if (c.type === 'sectionDivider') {
      // Calculate percentage complete
      let sectionFields = 0
      let completedSectionFields = 0
      for (let j = i + 1; j < components.length; j++) {
        const c2 = components[j]
        if (!c2 || c2.type === 'sectionDivider') break
        else if (c2.dataType && !c2.condition) {
          sectionFields += 1
          if (fieldHasValue(c2.dataType)) completedSectionFields += 1
        }
      }
      sections.push({ title: c.title, id: c.id, amountCompleted: sectionFields > 0 ? (completedSectionFields / sectionFields) : 1 })
    }
  })

  // Calculate percentage of the total form completed
  let totalFields = 0
  let completedFields = 0
  components?.forEach(c => {
    if (c.dataType && !c.condition) {
      totalFields += 1
      if (fieldHasValue(c.dataType)) completedFields += 1
    }
  })
  const amountCompleted = totalFields > 0 ? (completedFields / totalFields) : 1
  const noteRequired = !stage?.adHoc && ((!stage?.isRecurring && stageResponses?.length > 0) || (stage?.isRecurring && stageResponsesForRecurrence?.length > 0))
  return (
    <Modal
      isOpen={isOpen} onClose={closeModal} size='7xl'
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add data for {stage?.name || 'stage'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>

          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Box mb={3}>
              {stage?.adHoc
                ? <Alert status='info' variant='left-accent'>This stage is ad-hoc. You can submit as many responses to it as needed.</Alert>
                : (
                  <>
                    {!stage?.isRecurring && stageResponses?.length > 0 &&
                      <Alert status='warning' variant='left-accent'>This stage is not recurring and already has sumbmitted data. This means that any data you submit here will overwrite existing submitted data for this stage.</Alert>}
                    {stage?.isRecurring &&
                      <Box>
                        <Heading mb={1} size='sm'>Since this is a repeating (recurring) stage, choose a recurrence to add data for</Heading>
                        {stage?.isPoll && <Text fontSize='sm' color='gray.500'>Note: this stage has "poll mode" enabled. Data will not be overwritten even if re-editing the same recurrence.</Text>}
                        <Select placeholder='Select a recurrence' value={recurrence} onChange={e => setRecurrence(parseInt(e.target.value) || 0)}>
                          {[...Array(stage?.recurringDuration || 0)].map((i, index) =>
                            <option value={index} key={index}>
                              {index + 1}
                              {stageResponses?.filter(r => (r.stageRecurrence || 0) === (index || 0))?.length > 0 && ' (data has been submitted)'}
                            </option>
                          )}
                        </Select>
                        {stageResponsesForRecurrence?.length > 0 &&
                          <Alert mt={3} status='warning' variant='left-accent' fontSize='sm'>Data has already been submitted for this recurrence of this stage. Any changes you submit below will overwrite the existing submitted data for this recurrence.</Alert>}
                      </Box>}
                  </>)}
            </Box>
            <Box>
              {stageId &&
                <SimpleMenu
                  trigger={<Button isLoading={exporting} size='sm'>Download form <Icon as={FaCaretDown} /></Button>}
                  items={[
                    { icon: FaFilePdf, text: 'Download in PDF format', onClick: () => exportStage('pdf') },
                    { icon: FaFileWord, text: 'Download as a Word document', onClick: () => exportStage('docx') }
                  ]}
                />}
            </Box>
          </Box>

          <Divider mt={5} mb={5} />

          <Box position='relative'>
            <Box display='flex'>

              {sections?.length > 0 &&
                <Box minW={250} maxW={350} mr={2} position='relative'>
                  <Box shadow='md' bg='gray.100' rounded='md' position='sticky' style={{ top: 30 }} overflow='hidden'>
                    <Box display='flex' alignItems='center' mt={3} mb={5}>
                      <Heading ml={3} size='xs' textTransform='uppercase' textColor='gray'>Sections</Heading>
                      <Progress flex={1} ml={3} mr={3} bg='white' colorScheme={amountCompleted === 1 ? 'green' : 'orange'} value={amountCompleted * 100} size='xs' />
                    </Box>
                    <Box maxH='550px' overflowY='scroll'>
                      {sections.map(section =>
                        <Box
                          key={section.id}
                          p={3} mt={2} bg='gray.200' _hover={{ bg: 'gray.300' }} cursor='pointer'
                          display='flex' justifyContent='space-between' alignItems='center'
                          onClick={e => document.querySelector(`#component-${section.id}`)?.scrollIntoView({ behavior: 'smooth' })}
                        >
                          <Text mr={2}>{section.title}</Text>
                          <CircularProgress
                            value={section.amountCompleted * 100} size='30px' thickness='4px'
                            trackColor='white' color={section.amountCompleted === 1 ? 'green' : 'orange'}
                          >
                            {section.amountCompleted === 1 && <CircularProgressLabel><CheckCircleIcon height='20px' width='20px' color='green.500' /></CircularProgressLabel>}
                            {section.amountCompleted === 0 && <CircularProgressLabel><WarningIcon height='20px' width='20px' color='orange.500' /></CircularProgressLabel>}
                          </CircularProgress>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>}

              <Stack flex={1} spacing={5} mb={10}>
                {components?.map(c => {
                  if (!conditionIsMet(c)) return null
                  if (c.type === 'title') {
                    return (
                      <Box key={`component-${c.id}`} p={3} mt={10} mb={10} id={`component-${c.id}`}>
                        <Heading size='md'>{c.title}</Heading>
                      </Box>
                    )
                  }
                  if (c.type === 'sectionDivider') {
                    return (
                      <Box key={`component-${c.id}`} bg='blue.100' rounded='md' shadow='sm' p={3} mt={10} mb={10} id={`component-${c.id}`}>
                        <Heading size='lg'>{c.title}</Heading>
                        <Box mt={2}>
                          <RichTextViewer content={c.description} />
                        </Box>
                      </Box>
                    )
                  }
                  const Field = FIELDS[c.type?.type]

                  const value = response[c.dataType]?.value ?? existingResponse[c.dataType]?.value
                  const hasChangedValue = response[c.dataType]?.value ?? null
                  const hasValue = fieldHasValue(c.dataType)

                  const onChange = v => {
                    const newResponse = {
                      ...response,
                      [c.dataType]: {
                        value: v,
                        createdAt: new Date()
                      }
                    }
                    setResponse(newResponse)
                    if (!isPreview) {
                      saveDraft(newResponse)
                      setChangesMade(true)
                    }
                  }

                  return (
                    <FormControl key={c.id} p={3} borderWidth='1px' rounded='md' bg={errorField === c.id ? 'red.100' : 'gray.50'}>
                      <Box id={`field-entry-${c.id}`}>
                        <Box display='flex' justifyContent='end' mb={3}>
                          {hasChangedValue !== null
                            ? <Badge ml={3} colorScheme='green' size='sm'><CheckIcon /> This change will be included when submitted</Badge>
                            : <Badge ml={3} colorScheme='blue' size='sm'>No changes made</Badge>}
                          {c.isRequired && <Badge ml={3} colorScheme={hasValue ? 'green' : 'red'} size='sm'>{hasValue && <CheckCircleIcon color='green' />} Required field</Badge>}
                        </Box>
                        <Heading size='md' mb={2}>{c.title}</Heading>
                      </Box>
                      <RichTextViewer content={c.description} />

                      <Box mt={3}>
                        {Field
                          ? <Field dataType={c.type} value={value} onChange={onChange} participant={participant} />
                          : <Alert variant='left-accent'>This question type is not yet supported.</Alert>}
                      </Box>
                    </FormControl>
                  )
                })}
              </Stack>
            </Box>

            <Box position='sticky' style={{ bottom: 10 }} boxShadow='md' p={3} rounded='md' bg='gray.100'>
              {error &&
                <Alert mb={3} status='error' variant='left-accent'>
                  <Box>
                    <AlertTitle>There was a problem storing the stage data</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Box>
                </Alert>}

              <Stack direction={{ base: 'column', lg: 'row' }} spacing={3} justifyContent='end'>
                <Accordion allowToggle flex={1}>
                  <AccordionItem>
                    <AccordionButton>
                      <Heading size='xs'>Sign-off note
                        {noteRequired && !updateNote && <Badge ml={3} colorScheme='red' size='sm'>Required</Badge>}
                        {updateNote && <Badge ml={3} colorScheme='green' size='sm'><CheckIcon />Added</Badge>}
                      </Heading>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel>
                      <Textarea bg='white' size='sm' rows={1} placeholder='Write a note, justification, or information about this update...' value={updateNote} onChange={e => setUpdateNote(e.target.value)} />
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>

                <Box display='flex' justifyContent='end' alignItems='center'>
                  <Box>
                    {Object.keys(response || {}).length > 0 &&
                      <Popover>
                        <PopoverTrigger>
                          <Button ml={2} size='sm' variant='ghost' colorScheme='green'>
                            {changesMade
                              ? (
                                <>
                                  <CircularProgress color='orange.400' isIndeterminate size='18px' mr={2} />
                                  <Text color='gray.500' fontSize='sm'> Saving draft...</Text>
                                </>)
                              : (
                                <>
                                  <CheckCircleIcon color='green' mr={2} />
                                  {Object.keys(response || {}).length} changes saved as draft
                                </>)}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent>
                          <PopoverHeader size='sm'>The following fields will be included in your next submission</PopoverHeader>
                          <PopoverBody maxH={200} overflowY='scroll'>
                            <UnorderedList>
                              {Object.keys(response || {}).map(key => {
                                const fc = components.filter(c => c.dataType === key)
                                if (fc) return <ListItem key={key}>{fc[0]?.title}</ListItem>
                                return null
                              })}
                            </UnorderedList>
                          </PopoverBody>
                          <PopoverFooter textAlign='center'>
                            <ConfirmButton
                              header='Really revert your changes?'
                              body='Your progress will be lost and the form will be reset to the last submitted state.'
                              trigger={<Button ml={2} size='sm' variant='ghost' colorScheme='red'>Delete draft and clear changes</Button>}
                              action={revertDraft}
                            />
                          </PopoverFooter>
                        </PopoverContent>
                      </Popover>}
                    <Button size='sm' variant='ghost' onClick={closeModal}>Close without submitting</Button>
                    <ConfirmButton
                      header='Ready to submit?'
                      body={
                        <Box>
                          <Text>The updated data will be recorded for this participant.</Text>
                          <Box display='flex' justifyContent='space-between' alignItems='center' mt={3}>
                            <Heading size='sm'>Sign-off note</Heading>
                            {noteRequired && !updateNote && <Badge ml={3} colorScheme='red' size='sm'>Required</Badge>}
                          </Box>
                          <Textarea autoFocus mt={2} bg='white' size='sm' rows={2} placeholder='Write a note, justification, or information about this update...' value={updateNote} onChange={e => setUpdateNote(e.target.value)} />
                        </Box>
                      }
                      trigger={<Button size='sm' ml={2} colorScheme='primary' isDisabled={isPreview}>Submit data</Button>}
                      action={submit}
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>

          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
